import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  Checkbox,
  Input,
  DateTimePicker,
} from "../components/ui";
import { ArrowLeft } from "lucide-react";
import { formatDate } from "../utils/utils";
import {
  getClothPriceList,
  updateClothAndPriceListForOrder,
  completeDelivery,
  markOrderUrgent,
  markOrderNotUrgent,
  checkOnlinePaymentStatusOfOrder,
  generateQr,
  getTotalAmountOfOrder,
} from "../services/orders";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import orderStore from "../stores/OrderStore";
import { isObjectEmpty } from "../utils/utils";
import QRCode from "react-qr-code";
import { THRESHOLD_QR_RECREATION_MARGIN_DURATION_IN_MINS } from "../constants/values";

export const OrderDetailsScreen = () => {
  const [priceList, setPriceList] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [quantities, setQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [washAndFoldQuantity, setWashAndFoldQuantity] = useState(0);
  const [washAndIronQuantity, setWashAndIronQuantity] = useState(0);
  const [premiumWashAndIronQuantity, setPremiumWashAndIronQuantity] =
    useState(0);
  const navigate = useNavigate();
  const [submittingClothDetails, setSubmittingClothDetails] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [chaningUrgent, setChangingUrgent] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [checkingOnlinePaymentStatus, setCheckingOnlinePaymentStatus] =
    useState(false);
  const [onlinePaymentStatusSuccess, setOnlinePaymentStatusSuccess] =
    useState(false);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [totalAmount, setTotalAmount] = useState(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const { orderId } = useParams();

  const fetchPriceList = async () => {
    setIsLoading(true);

    getClothPriceList()
      .then((data) => {
        setPriceList(data.cloth_price_list);
        setIsLoading(false);
      })
      .catch((err) => {
        setError("Failed to fetch price list. Please try again later.");
        setIsLoading(false);
      });
  };

  const checkOnlinePaymentStatus = () => {
    setCheckingOnlinePaymentStatus(true);
    checkOnlinePaymentStatusOfOrder(orderId)
      .then((data) => {
        setCheckingOnlinePaymentStatus(false);
        if (data.payment_status === 1 && data.payment_mode === 0) {
          setOnlinePaymentStatusSuccess(true);
        } else {
          setOnlinePaymentStatusSuccess(false);
        }
      })
      .catch((err) => {
        setCheckingOnlinePaymentStatus(false);
        setOnlinePaymentStatusSuccess(false);
      });
  };

  const getOrderByIdFromStore = (orderId) => {
    return orderStore.orders.find((order) => order.id === orderId);
  };

  const fetchTotalAmountOfOrder = useCallback(() => {
    getTotalAmountOfOrder(orderId)
      .then((data) => {
        setTotalAmount(data.total_amount);
      })
      .catch((err) => {});
  }, [orderId]);

  useEffect(() => {
    setSelectedOrder(getOrderByIdFromStore(orderId));
    fetchPriceList();
    fetchTotalAmountOfOrder();
  }, [orderId, fetchTotalAmountOfOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDateTimePicker &&
        !event.target.closest(".relative") &&
        !event.target.closest('[class*="absolute"]')
      ) {
        setShowDateTimePicker(false);
        setSelectedDateTime(null);
      }
    };

    if (showDateTimePicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDateTimePicker]);

  useEffect(() => {
    if (selectedOrder && selectedOrder.clothes_and_prices) {
      setWashAndFoldQuantity(
        selectedOrder.clothes_and_prices.wash_and_fold_quantity || 0,
      );
      setWashAndIronQuantity(
        selectedOrder.clothes_and_prices.wash_and_iron_quantity || 0,
      );
      setPremiumWashAndIronQuantity(
        selectedOrder.clothes_and_prices.premium_wash_and_iron_quantity || 0,
      );
      const prevSelectedItems =
        selectedOrder.clothes_and_prices.clothes_list.reduce((acc, item) => {
          acc[item.cloth_name] = true;
          return acc;
        }, {});

      const prevQuantities =
        selectedOrder.clothes_and_prices.clothes_list.reduce((acc, item) => {
          acc[item.cloth_name] = item.quantity;
          return acc;
        }, {});
      setSelectedItems({ ...selectedItems, ...prevSelectedItems });
      setQuantities({ ...quantities, ...prevQuantities });
      setDiscount(selectedOrder.clothes_and_prices.gross_discount || 0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);

  const handleCheckboxChange = (clothName) => {
    if (selectedItems[clothName]) {
      let quantitiesWithDeletedItem = { ...quantities };
      delete quantitiesWithDeletedItem[clothName];
      setQuantities(quantitiesWithDeletedItem);
    } else {
      setQuantities({ ...quantities, [clothName]: 0 });
    }
    setSelectedItems((prev) => ({
      ...prev,
      [clothName]: !prev[clothName],
    }));
  };

  const handleQuantityChange = (clothName, value) => {
    let quantity = Number(value);
    if (
      clothName === "wash_and_fold" ||
      clothName === "wash_and_iron" ||
      clothName === "premium_wash_and_iron"
    ) {
      quantity = Math.ceil(quantity / 0.5) * 0.5;
    } else {
      quantity = Math.ceil(quantity);
    }
    setQuantities((prev) => ({
      ...prev,
      [clothName]: quantity,
    }));
  };

  const areSelectedPricesValid = () => {
    return Object.values(quantities).every((quantity) => quantity > 0);
  };

  const isValidWashFoldAndIronQuantity = () => {
    if ("wash_and_fold" in quantities) {
      if (washAndFoldQuantity <= 0 || !Number.isInteger(washAndFoldQuantity)) {
        return false;
      }
    }

    if ("premium_wash_and_iron" in quantities) {
      if (
        premiumWashAndIronQuantity <= 0 ||
        !Number.isInteger(premiumWashAndIronQuantity)
      ) {
        return false;
      }
    }

    if ("wash_and_iron" in quantities) {
      if (washAndIronQuantity <= 0 || !Number.isInteger(washAndIronQuantity)) {
        return false;
      }
    }

    return true;
  };

  const calculateItemCost = (item) => {
    const quantity = quantities[item.cloth_name] || 0;
    const price = parseFloat(item.discounted_price) || parseFloat(item.price);
    return quantity * price;
  };

  const markDelviered = () => {
    setMarkingComplete(true);
    completeDelivery(orderId, paymentMethod === "online" ? 0 : 1)
      .then((data) => {
        setMarkingComplete(false);
        navigate("/");
      })
      .catch((err) => {
        setMarkingComplete(false);
      });
  };

  const calculateTotalCost = () => {
    return priceList
      .filter((item) => selectedItems[item.cloth_name])
      .reduce((total, item) => total + calculateItemCost(item), 0);
  };

  const filteredPriceList = priceList.filter((item) =>
    item.display_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const submitClothDetails = () => {
    setSubmittingClothDetails(true);
    const clothes_list = {
      clothes_list: Object.keys(quantities).map((itemName) => {
        return {
          display_name: priceList.find(
            (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
          ).display_name,
          cloth_name: itemName,
          price: Number(
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).price,
          ),
          discounted_price: Number(
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).discounted_price,
          ),
          quantity_pricing:
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).quantity_pricing || [],
          quantity: quantities[itemName],
          key: itemName,
          type:
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).type || "DC",
          pieces:
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).pieces || 1,
          os:
            priceList.find(
              (clothPriceDetail) => clothPriceDetail.cloth_name === itemName,
            ).os || false,
        };
      }),
      gross_discount: discount || 0,
      wash_and_fold_quantity: washAndFoldQuantity,
      wash_and_iron_quantity: washAndIronQuantity,
      premium_wash_and_iron_quantity: premiumWashAndIronQuantity,
    };
    updateClothAndPriceListForOrder(orderId, clothes_list)
      .then((data) => {
        setSubmittingClothDetails(false);
        navigate(`invoice`, {
          state: {
            priceList: priceList,
            washAndFoldQuantity: washAndFoldQuantity,
            washAndIronQuantity: washAndIronQuantity,
            premiumWashAndIronQuantity: premiumWashAndIronQuantity,
            quantities: quantities,
            orderId: orderId,
            discount: discount,
          },
        });
      })
      .catch((err) => {
        setSubmittingClothDetails(false);
      });
  };

  const markUrgent = (preferred_datetime = null) => {
    setChangingUrgent(true);
    markOrderUrgent(orderId, preferred_datetime)
      .then((data) => {
        setChangingUrgent(false);
        setSelectedOrder({
          ...selectedOrder,
          meta_data: selectedOrder.meta_data
            ? { ...selectedOrder.meta_data, urgent: true }
            : { urgent: true },
        });
        setShowDateTimePicker(false);
        setSelectedDateTime(null);
      })
      .catch((err) => {
        setChangingUrgent(false);
      });
  };

  const handleDateTimeSelect = (dateTime) => {
    setSelectedDateTime(dateTime);
    markUrgent(dateTime);
  };

  const handleDateTimeCancel = () => {
    setShowDateTimePicker(false);
    setSelectedDateTime(null);
  };

  const markNotUrgent = () => {
    setChangingUrgent(true);
    markOrderNotUrgent(orderId)
      .then((data) => {
        setChangingUrgent(false);
        setSelectedOrder({
          ...selectedOrder,
          meta_data: selectedOrder.meta_data
            ? { ...selectedOrder.meta_data, urgent: false }
            : { urgent: false },
        });
      })
      .catch((err) => {
        setChangingUrgent(false);
      });
  };

  const fetchQr = (orderId) => {
    setGeneratingQr(true);
    generateQr(orderId)
      .then((data) => {
        setGeneratingQr(false);
        setSelectedOrder({
          ...selectedOrder,
          payment_meta_data: data.payment_meta_data,
        });
      })
      .catch((err) => {
        setGeneratingQr(false);
      });
  };

  const renderOrderStatus1 = () => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      {isLoading && <p className="text-gray-600">Loading price list...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {filteredPriceList.map((item) => (
        <Card key={item.cloth_name} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Checkbox
                checked={selectedItems[item.cloth_name] || false}
                onCheckedChange={() => handleCheckboxChange(item.cloth_name)}
              />
              <div>
                <p className="font-medium">{item.display_name}</p>
                <p className="text-sm text-gray-600">
                  Rate: ₹{item.discounted_price || item.price}
                </p>
              </div>
            </div>

            {selectedItems[item.cloth_name] && (
              <div className="flex items-center space-x-4">
                <Input
                  type="number"
                  placeholder="Qty"
                  value={quantities[item.cloth_name] || ""}
                  onChange={(e) =>
                    handleQuantityChange(item.cloth_name, e.target.value)
                  }
                  className="w-20"
                />
                {(item.cloth_name === "wash_and_fold" ||
                  item.cloth_name === "wash_and_iron" ||
                  item.cloth_name === "premium_wash_and_iron") && (
                  <Input
                    type="number"
                    placeholder="Pcs."
                    value={
                      item.cloth_name === "wash_and_fold"
                        ? washAndFoldQuantity
                        : item.cloth_name === "wash_and_iron"
                          ? washAndIronQuantity
                          : item.cloth_name === "premium_wash_and_iron"
                            ? premiumWashAndIronQuantity
                            : 0
                    }
                    onChange={(e) => {
                      if (item.cloth_name === "wash_and_fold") {
                        setWashAndFoldQuantity(Number(e.target.value));
                      } else if (item.cloth_name === "wash_and_iron") {
                        setWashAndIronQuantity(Number(e.target.value));
                      } else if (item.cloth_name === "premium_wash_and_iron") {
                        setPremiumWashAndIronQuantity(Number(e.target.value));
                      }
                    }}
                    className="w-20"
                  />
                )}
                <p className="font-medium">₹{calculateItemCost(item)}</p>
              </div>
            )}
          </div>
        </Card>
      ))}

      {filteredPriceList.length > 0 && (
        <div className="mt-6 flex justify-between items-center border-t pt-4">
          <p className="text-lg font-bold">Total Cost</p>
          <p className="text-lg font-bold">₹{calculateTotalCost()}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4">
      <Button
        variant="ghost"
        onClick={() => {
          navigate("/");
        }}
        className="mb-4 flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Orders
      </Button>

      {selectedOrder && (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-bold">{selectedOrder.full_name}</h2>
            <p className="text-gray-600">{selectedOrder.address_1}</p>
            {selectedOrder.landmark && (
              <p className="text-gray-500">
                Landmark: {selectedOrder.landmark}
              </p>
            )}
          </div>
          <div className="mb-4">
            <h3 className="font-medium mb-2">Order Details</h3>
            <p className="text-sm text-gray-600">
              Created: {formatDate(selectedOrder.created_at)}
            </p>
            {!isLoading ? (
              <>
                <div className="flex justify-between">
                  <p>Total:</p>
                  {selectedOrder.status > 2 ? (
                    <p>₹{totalAmount}</p>
                  ) : (
                    <p>₹{calculateTotalCost()}</p>
                  )}
                </div>
              </>
            ) : (
              <></>
            )}
            <div className="flex justify-between mt-10">
              {selectedOrder.status <= 2 && (
                <>
                  <Button
                    className="mt-5"
                    onClick={() => {
                      submitClothDetails();
                    }}
                    disabled={
                      !areSelectedPricesValid() ||
                      isObjectEmpty(selectedItems) ||
                      isObjectEmpty(quantities) ||
                      !isValidWashFoldAndIronQuantity() ||
                      submittingClothDetails ||
                      isLoading
                    }
                  >
                    {submittingClothDetails ? `Loading` : `Generate Invoice`}
                  </Button>
                  {selectedOrder.meta_data &&
                  "urgent" in selectedOrder.meta_data &&
                  selectedOrder.meta_data.urgent ? (
                    <Button
                      className="mt-5"
                      disabled={chaningUrgent}
                      onClick={() => {
                        markNotUrgent();
                      }}
                    >
                      Make Not Urgent
                    </Button>
                  ) : (
                    <div>
                      <Button
                        className="mt-5"
                        disabled={chaningUrgent}
                        onClick={() => {
                          setShowDateTimePicker(true);
                        }}
                      >
                        Make Urgent
                      </Button>
                      {showDateTimePicker && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm">
                            <DateTimePicker
                              value={selectedDateTime}
                              onChange={handleDateTimeSelect}
                              onClose={handleDateTimeCancel}
                              className="relative"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {selectedOrder.status > 2 && (
                <>
                  <div>
                    <div>
                      <Button
                        variant="default"
                        onClick={() => fetchQr(selectedOrder.id)}
                        disabled={generatingQr}
                      >
                        {generatingQr ? `Loading...` : `Generate QR Code`}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="mt-5"
                        onClick={() => setPaymentMethod("cash")}
                        variant={
                          paymentMethod === "cash" ? "default" : "outline"
                        }
                        disabled={checkingOnlinePaymentStatus}
                      >
                        Cash
                      </Button>
                      <Button
                        className="mt-5"
                        onClick={() => {
                          setPaymentMethod("online");
                          checkOnlinePaymentStatus();
                        }}
                        variant={
                          paymentMethod === "online" ? "default" : "outline"
                        }
                        disabled={
                          checkingOnlinePaymentStatus ||
                          selectedOrder.payment_meta_data?.FORCE_OFFLINE
                        }
                        loading={checkingOnlinePaymentStatus}
                      >
                        {checkingOnlinePaymentStatus
                          ? "Loading..."
                          : "Check Online"}
                      </Button>
                    </div>
                    {paymentMethod === "online" &&
                      !checkingOnlinePaymentStatus && (
                        <div className="mt-2 flex items-center gap-2">
                          {onlinePaymentStatusSuccess ? (
                            <>
                              <span className="text-green-600">✓</span>
                              <span className="text-green-600">
                                Payment Successful
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-red-600">✗</span>
                              <span className="text-red-600">
                                Payment Failed
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    <Button
                      disabled={
                        markingComplete ||
                        !paymentMethod ||
                        checkingOnlinePaymentStatus ||
                        (paymentMethod === "online" &&
                          !onlinePaymentStatusSuccess)
                      }
                      onClick={() => {
                        markDelviered();
                      }}
                      className="mt-5"
                    >
                      Mark Complete
                    </Button>
                  </div>
                </>
              )}
            </div>
            {selectedOrder.payment_meta_data &&
              selectedOrder.payment_meta_data.QR_TEXT &&
              selectedOrder.payment_meta_data.QR_VALIDITY &&
              Math.floor(Date.now() / 1000) <
                selectedOrder.payment_meta_data.QR_VALIDITY -
                  THRESHOLD_QR_RECREATION_MARGIN_DURATION_IN_MINS * 60 &&
              selectedOrder.status > 2 &&
              !generatingQr &&
              !selectedOrder.payment_meta_data?.FORCE_OFFLINE && (
                <div className="mt-10 flex justify-center">
                  <QRCode
                    value={selectedOrder.payment_meta_data.QR_TEXT}
                    size={256}
                    style={{ height: "auto", maxWidth: "80%", width: "80%" }}
                  />
                </div>
              )}
            {selectedOrder.order_remark && (
              <p className="text-sm text-gray-600 mt-1">
                Remarks: {selectedOrder.order_remark}
              </p>
            )}
          </div>

          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mb-4"
          />

          {selectedOrder.status <= 2 ? renderOrderStatus1() : <></>}
        </>
      )}
    </div>
  );
};
